// src/utils/filterUtils.js
// Shared filtering helpers used by the report tables, page filters and
// dropdowns. Centralising these fixes two recurring bugs:
//   1. Date filters off by ±1 day (timezone-sensitive parsing of API dates).
//   2. Text search matching across word boundaries (whitespace was stripped).

import { format } from "date-fns";

// ---------------------------------------------------------------------------
// Dates
// ---------------------------------------------------------------------------

// Extract the *calendar* date (year/month/day) from any value and return a
// Date pinned to LOCAL midnight. This is timezone-safe for day-level filtering:
// it never lets a UTC offset push a record into the previous/next day.
//
// Accepts: Date objects, "yyyy-MM-dd", "yyyy-MM-ddTHH:mm:ss[Z]", ISO strings,
// or "dd-MM-yyyy". Returns null for empty/invalid input.
export const toCalendarDate = (value) => {
  if (!value) return null;

  if (value instanceof Date) {
    if (isNaN(value.getTime())) return null;
    return new Date(value.getFullYear(), value.getMonth(), value.getDate());
  }

  if (typeof value !== "string") return null;
  const str = value.trim();
  if (!str) return null;

  // Pull the leading yyyy-MM-dd date portion if present (ignoring any time/zone).
  const iso = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) {
    const [, y, m, d] = iso;
    return new Date(Number(y), Number(m) - 1, Number(d));
  }

  // dd-MM-yyyy or dd/MM/yyyy
  const dmy = str.match(/^(\d{2})[-/](\d{2})[-/](\d{4})/);
  if (dmy) {
    const [, d, m, y] = dmy;
    return new Date(Number(y), Number(m) - 1, Number(d));
  }

  // Fallback: let the engine parse it, then strip the time component.
  const parsed = new Date(str);
  if (isNaN(parsed.getTime())) return null;
  return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
};

// True when `value`'s calendar day falls within [start, end] (inclusive).
// `start`/`end` may be Date objects or date strings; either bound may be null.
export const isWithinDateRange = (value, start, end) => {
  const day = toCalendarDate(value);
  if (!day) return false;
  const time = day.getTime();

  const startDay = toCalendarDate(start);
  const endDay = toCalendarDate(end);

  if (startDay && time < startDay.getTime()) return false;
  if (endDay && time > endDay.getTime()) return false;
  return true;
};

// Format a Date/value as "yyyy-MM-dd" for API payloads (null-safe).
export const toApiDate = (value) => {
  const day = toCalendarDate(value);
  return day ? format(day, "yyyy-MM-dd") : null;
};

// Build the standard { FromDate, ToDate } payload most list APIs expect.
// `keys` lets callers match an API that uses different casing (e.g. fromDate).
export const buildDateRangePayload = (
  start,
  end,
  keys = { from: "FromDate", to: "ToDate" }
) => ({
  [keys.from]: toApiDate(start),
  [keys.to]: toApiDate(end),
});

// ---------------------------------------------------------------------------
// Text
// ---------------------------------------------------------------------------

// Normalise a string for comparison: lower-cased, collapsed internal
// whitespace, trimmed. Unlike the old approach this keeps word boundaries,
// so searching "John" no longer leaks across "John Doe" → "JohnDoe".
export const normalizeText = (value) => {
  if (value == null) return "";
  return String(value).toLowerCase().replace(/\s+/g, " ").trim();
};

// Case-insensitive, whitespace-tolerant matchers.
export const textContains = (value, query) => {
  const q = normalizeText(query);
  if (!q) return true; // empty query matches everything
  return normalizeText(value).includes(q);
};

export const textEquals = (value, query) =>
  normalizeText(value) === normalizeText(query);

export const textStartsWith = (value, query) => {
  const q = normalizeText(query);
  if (!q) return true;
  return normalizeText(value).startsWith(q);
};

// ---------------------------------------------------------------------------
// Advanced filters (the "Advanced Filters" dialog)
// ---------------------------------------------------------------------------

// Does one row satisfy every condition in the dialog's list?
//
// Lives here rather than inside the grid because the report page has no grid:
// it renders the printable document straight from the data, and still has to
// answer the same question about the same conditions. Two copies of this would
// be two chances for the report on screen to disagree with the grid it came
// from — over a date boundary, most likely, which is exactly the bug this
// module exists to stop.
//
//   row        one record
//   filterList [{ column, operator, value, startDate, endDate }]
//   columns    the column definitions, for `type` and `searchValue`
//
// An empty list matches everything, so a caller can pass it unconditionally.
export const matchesAdvancedFilters = (row, filterList = [], columns = []) =>
  filterList.every((filter) => {
    const colDef = columns.find((c) => c.key === filter.column);
    const isDate = colDef?.type === "date" || colDef?.type === "datetime";

    if (isDate) {
      // Timezone-safe, day-level comparison (see toCalendarDate).
      const rawDate = colDef?.filterDateValue
        ? colDef.filterDateValue(row)
        : row[filter.column];
      const rowDay = toCalendarDate(rawDate);
      if (!rowDay) return false;

      if (filter.operator === "equals") {
        const s = toCalendarDate(filter.startDate);
        return s ? rowDay.getTime() === s.getTime() : true;
      }
      // "between" (and any other date operator) — inclusive range
      return isWithinDateRange(rowDay, filter.startDate, filter.endDate);
    }

    if (!filter.value) return true;

    let raw = colDef?.searchValue
      ? colDef.searchValue(row)
      : row[filter.column];
    if (typeof raw === "boolean") raw = raw ? "yes" : "no";

    if (filter.operator === "contains") return textContains(raw, filter.value);
    if (filter.operator === "equals") return textEquals(raw, filter.value);
    if (filter.operator === "notEquals") return !textEquals(raw, filter.value);
    if (filter.operator === "startsWith")
      return textStartsWith(raw, filter.value);

    return true;
  });

// ---------------------------------------------------------------------------
// Column filter operators (DevExpress-style header filter row)
// ---------------------------------------------------------------------------

// Operator list rendered in the header filter operator menu. `glyph` is the
// tiny hint shown inside the operator button; `label` is the menu text.
export const TEXT_OPERATORS = [
  { value: "contains", label: "Contains", glyph: "∋" },
  { value: "notContains", label: "Does not contain", glyph: "∌" },
  { value: "equals", label: "Equals", glyph: "=" },
  { value: "notEquals", label: "Does not equal", glyph: "≠" },
  { value: "isLike", label: "Is like", glyph: "≈" },
  { value: "isNotLike", label: "Is not like", glyph: "≉" },
  { value: "beginsWith", label: "Begins with", glyph: "⤷" },
  { value: "endsWith", label: "Ends with", glyph: "⤶" },
];

export const DEFAULT_TEXT_OPERATOR = "contains";

export const getOperatorMeta = (op) =>
  TEXT_OPERATORS.find((o) => o.value === op) || TEXT_OPERATORS[0];

// Convert a SQL-style LIKE pattern (%, _) into a case-insensitive RegExp.
// With no wildcards it behaves like "contains".
const likeToRegex = (pattern) => {
  const escaped = String(pattern).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const withWildcards = escaped.replace(/%/g, ".*").replace(/_/g, ".");
  return new RegExp(withWildcards, "i");
};

// Apply a text operator. An empty query always matches (no filter set).
export const matchTextOperator = (rawValue, op, query) => {
  const q = normalizeText(query);
  if (!q) return true;

  const v = normalizeText(rawValue);
  switch (op) {
    case "equals":
      return v === q;
    case "notEquals":
      return v !== q;
    case "notContains":
      return !v.includes(q);
    case "beginsWith":
      return v.startsWith(q);
    case "endsWith":
      return v.endsWith(q);
    case "isLike":
      return likeToRegex(query).test(String(rawValue ?? ""));
    case "isNotLike":
      return !likeToRegex(query).test(String(rawValue ?? ""));
    case "contains":
    default:
      return v.includes(q);
  }
};
